import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { Tabs, Tab } from 'components/tabs'
import LinearProgress from '@material-ui/core/LinearProgress'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme, makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import { useAuth } from 'lib/auth'
import { PageHeader } from 'components/page-header'
import SchedulePage from './schedule'
import OfferingsPage, {
  OFFERINGS_TAB,
  getOfferingsPath,
  parseOfferingsTab,
} from './offerings'
import OfferingsHeaderActions from './offerings/offerings-header-actions'
import useGroupEventsPanel from './events/use-group-events-panel'
import { CreateEventQuickStartOverlay } from './events/create-event-form/form-sections/create-event-quick-start'
import { gtmEvent } from 'lib/gtm'
import GET_COACH_ACCOUNT from './query'
import ScheduleHeaderActions from './schedule-header-actions'
import { useCoachBookStudentNavigation } from 'lib/pga-coach/use-coach-fast-book-student'

const useStyles = makeStyles(() => ({
  buttonsWrapper: {
    display: 'flex',
    marginRight: 0,
    width: 'auto',
    paddingLeft: 0,
    justifyContent: 'flex-end',
    flexWrap: 'nowrap',
    gap: '10px',
  },
}))

const BookingsPage = () => {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const theme = useTheme()
  const { user, setUserProperties } = useAuth()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isSchedulePage = /\/bookings\/schedule\/?$/.test(location.pathname)
  const isOfferingsPage = !isSchedulePage
  const pageName = isSchedulePage ? 'Schedule' : 'My Offerings'
  const { data, loading, error } = useQuery(GET_COACH_ACCOUNT)
  const [scheduleSubtitle, setScheduleSubtitle] = useState('')

  const handleScheduleMetadataChange = useCallback(({ rangeLabel }) => {
    setScheduleSubtitle(rangeLabel)
  }, [])
  const [offeringsTab, setOfferingsTab] = useState(OFFERINGS_TAB.BOOKING_TYPE)
  const [
    shouldShowBookingTypeDisclaimer,
    setShouldShowBookingTypeDisclaimer,
  ] = useState(false)
  const groupEventsPanel = useGroupEventsPanel({ skip: !isOfferingsPage })
  const {
    isCoachFastBookEnabled,
    navigateToBookStudent,
  } = useCoachBookStudentNavigation()

  const onboardingSchedule = useMemo(
    () => ({
      isFacilitiesCreated:
        (data?.currentUser?.attributes?.facilities?.length ?? 0) > 0,
      isGroupEventCreated:
        (data?.currentUser?.groupEvents?.filter(e => !e.isCanceled)?.length ??
          0) > 0,
      isAvailable: data?.coachAvailabilities?.some(
        a => (a.timeSlots?.length ?? 0) > 0,
      ),
      isBookingTypeCreated: (data?.lessonTypes?.length ?? 0) > 0,
      hasCoachProfile: data?.currentUser?.attributes?.coachProfile != null,
    }),
    [data],
  )

  const isOnboardingComplete =
    onboardingSchedule.isGroupEventCreated ||
    onboardingSchedule.isBookingTypeCreated

  const handleBookStudentClick = () => {
    gtmEvent({
      event: 'formSubmit',
      formCategory: 'book-session',
      formAction: 'clicked-book-session-button',
    })
    if (isCoachFastBookEnabled) {
      navigateToBookStudent(history)
    }
  }

  const scheduleHeaderActions = (
    <ScheduleHeaderActions
      bookingUrl={user?.coach?.bookingUrl}
      isOnboardingComplete={isOnboardingComplete}
      onBookStudentClick={handleBookStudentClick}
      useCoachFastBookStudent={isCoachFastBookEnabled}
    />
  )

  const handleCreateBookingTypeClick = () => {
    if (!canCreateBookingType) return
    setUserProperties({ onboarding: false })
    history.push('/pga-coach/bookings/booking-types/new')
  }

  const createBookingTypeDisabledReason = loading
    ? ''
    : (() => {
      if (!onboardingSchedule.hasCoachProfile) {
        return 'Create your coach profile before adding a booking type.'
      }
      if (!onboardingSchedule.isAvailable) {
        return 'Set your availability before creating a booking type.'
      }
      if (shouldShowBookingTypeDisclaimer) {
        return 'To add another offering, you need to upgrade your PGA Coach account.'
      }
      return ''
    })()

  const canCreateBookingType = !loading && !createBookingTypeDisabledReason

  const actionButton = isSchedulePage ? (
    scheduleHeaderActions
  ) : isOfferingsPage ? (
    <OfferingsHeaderActions
      user={user}
      isOnboardingComplete={isOnboardingComplete}
      isBookingTypeTab={offeringsTab === OFFERINGS_TAB.BOOKING_TYPE}
      canCreateBookingType={canCreateBookingType}
      createBookingTypeDisabledReason={createBookingTypeDisabledReason}
      onCreateBookingType={handleCreateBookingTypeClick}
      onCreateEvent={groupEventsPanel.handleCreateEventClick}
      onBookStudentClick={handleBookStudentClick}
      useCoachFastBookStudent={isCoachFastBookEnabled}
    />
  ) : null

  const actions = actionButton ? (
    <Box className={classes.buttonsWrapper}>{actionButton}</Box>
  ) : null
  const isMobileActionEmpty = (() => {
    if (!actionButton) return true
    if (actionButton.type === OfferingsHeaderActions) return false
    const { children } = actionButton.props
    if (!children) return true
    return React.Children.toArray(children).every(child => child === null)
  })()

  useEffect(() => {
    if (!isOfferingsPage) return

    const tabFromUrl = parseOfferingsTab(location.search)
    setOfferingsTab(tabFromUrl ?? OFFERINGS_TAB.BOOKING_TYPE)
  }, [isOfferingsPage, location.search])

  if (loading) return <LinearProgress color="primary" />

  if (error) {
    return (
      <Box p={3}>
        <PageHeader title="Schedule" />
        <Typography color="error">
          We couldn&apos;t load your schedule. Please refresh and try again.
        </Typography>
      </Box>
    )
  }

  return (
    <>
      <PageHeader
        title={pageName}
        subtitle={isSchedulePage ? scheduleSubtitle : undefined}
        tabs={
          isOfferingsPage ? (
            <Tabs
              value={offeringsTab}
              onChange={(_, value) => {
                setOfferingsTab(value)
                history.replace(getOfferingsPath(value))
              }}
            >
              <Tab label="Booking Type" />
              <Tab label="Group Events" />
            </Tabs>
          ) : null
        }
        actions={actions}
        fixedMobileActions={!isSchedulePage && isMobile && !isMobileActionEmpty}
      />
      {isSchedulePage ? (
        <SchedulePage
          onboardingSchedule={onboardingSchedule}
          onMetadataChange={handleScheduleMetadataChange}
        />
      ) : isOfferingsPage ? (
        <OfferingsPage
          selectedTab={offeringsTab}
          hasCoachProfile={onboardingSchedule.hasCoachProfile}
          setShouldShowBookingTypeDisclaimer={
            setShouldShowBookingTypeDisclaimer
          }
          groupEventsPanel={groupEventsPanel}
        />
      ) : null}
      {groupEventsPanel.showCreateEventQuickStart ? (
        <CreateEventQuickStartOverlay
          canBuildOwnEvent={!groupEventsPanel.showLimitReachedDisclaimer}
          onContinue={groupEventsPanel.handleCreateEventQuickStartContinue}
          onCancel={groupEventsPanel.handleCreateEventQuickStartCancel}
          onSourceSelected={
            groupEventsPanel.handleCreateEventQuickStartSourceSelected
          }
        />
      ) : null}
    </>
  )
}

export default BookingsPage
